import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import debounce from 'lodash/debounce';

import { useGetAccounts, useGetCareDrivers, useGetRiders } from '../../../hooks/useSlugSearchQuery';

export interface FilteredOption {
  label: string;
  [x: string]: string | number | boolean | string[];
}

interface IUseSlugSearch {
  filteredOptions: FilteredOption[];
  isFetching: boolean;
  isSuccess: boolean;
  isLoading: boolean;
  searchBySlug: (slug: string) => void;
  fetchNextPage: () => void;
}

const recordsPerPage = 10;
const queryInitialState = { q: '', page: 1, per_page: recordsPerPage };
const queryMapper = {
  account: 'getAccounts',
  rider: 'getRiders',
  careDriver: 'getCareDrivers',
};

export const useSlugSearch = (name): IUseSlugSearch => {
  const [query, setQuery] = useState(queryInitialState);
  const [filteredOptions, setFilteredOptions] = useState<FilteredOption[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [lastRequestReason, setLastRequestReason] = useState('init');
  const {
    data: accountsData,
    isFetching: isFetchingAccounts,
    isSuccess: hasFetchedAccounts,
  } = useGetAccounts(query, enabled && name === 'account');
  const {
    data: careDriversData,
    isFetching: isFetchingCareDrivers,
    isSuccess: hasFetchedCareDrivers,
  } = useGetCareDrivers(query, enabled && name === 'careDriver');
  const {
    data: ridersData,
    isFetching: isFetchingRiders,
    isSuccess: hasFetchedRiders,
  } = useGetRiders(query, enabled && name === 'rider');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hitMaxPage, setHitMaxPage] = useState<boolean>(false);
  const queryClient = useQueryClient();

  const debouncedSearchBySlug = useCallback(
    debounce((slug: string) => {
      if (slug) {
        setEnabled(true);
        setLastRequestReason('search');
        setQuery({ q: slug, page: 1, per_page: recordsPerPage });
      } else {
        setEnabled(false);
        setLastRequestReason('init');
        setQuery(queryInitialState);
        setFilteredOptions([]);
        queryClient.removeQueries({ queryKey: [queryMapper[name]] });
      }
    }, 500),
    []
  );

  const debouncedFetchNextPage = useCallback(
    debounce(() => {
      setLastRequestReason('nextPage');
      setQuery((prevQuery) => ({
        ...prevQuery,
        page: prevQuery.page + 1,
      }));
    }, 500),
    []
  );

  const searchBySlug = (slug: string) => {
    setIsLoading(!!slug);
    setHitMaxPage(false);
    debouncedSearchBySlug(slug);
  };

  const fetchNextPage = () => {
    setIsLoading(!hitMaxPage);
    if (!hitMaxPage) {
      debouncedFetchNextPage();
    }
  };

  const appendOrReplaceOptions = (newOptions: FilteredOption[], totalCount: number | null) => {
    if (lastRequestReason === 'search') {
      setFilteredOptions(newOptions);
    } else if (lastRequestReason === 'nextPage') {
      setFilteredOptions((prevValue) => [...prevValue, ...newOptions]);

      if (newOptions.length < recordsPerPage || (totalCount && filteredOptions.length >= totalCount)) {
        setHitMaxPage(true);
      }
    } else if (lastRequestReason === 'init') {
      setFilteredOptions([]);
    }
  };

  useEffect(() => {
    setIsLoading(false);

    switch (name) {
      case 'account':
        if (accountsData) {
          appendOrReplaceOptions(
            accountsData.data.map((account) => ({
              value: account.id,
              label: account.name,
            })),
            accountsData.meta.totalCount
          );
        }
        break;
      case 'careDriver':
        if (careDriversData) {
          appendOrReplaceOptions(
            careDriversData.data.map((driver) => ({
              value: driver.id,
              label: `${driver.firstName} ${driver.lastName}`,
            })),
            null
          );
        }
        break;
      case 'rider':
        if (ridersData) {
          appendOrReplaceOptions(
            ridersData.data.map((rider) => ({
              value: rider.id,
              label: `${rider.firstName} ${rider.lastName}`,
            })),
            ridersData.meta.totalCount
          );
        }
        break;
      default:
        setFilteredOptions([]);
    }
  }, [accountsData, careDriversData, ridersData, name]);

  const isFetching = isFetchingAccounts || isFetchingCareDrivers || isFetchingRiders;
  const isSuccess = hasFetchedAccounts || hasFetchedCareDrivers || hasFetchedRiders;

  return { filteredOptions, searchBySlug, fetchNextPage, isFetching, isSuccess, isLoading };
};
